import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getPagePreferences, savePagePreferences } from "../utilities/functions";

type PagePreferenceValue =
  | string
  | number
  | boolean
  | null
  | Record<string, unknown>
  | unknown[];

type PagePreferences = Record<string, PagePreferenceValue>;

function getUserId() {
  return Number(localStorage.getItem("userid") || 0);
}

function getStorageKey(idUser: number, pageKey: string) {
  return `uiPref:v1:${idUser}:${pageKey}`;
}

function readLocalPreferences<T extends PagePreferences>(
  idUser: number,
  pageKey: string,
  defaults: T
): T {
  if (!idUser) return defaults;

  try {
    const raw = localStorage.getItem(getStorageKey(idUser, pageKey));
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    return { ...defaults, ...parsed };
  } catch {
    return defaults;
  }
}

export function usePagePreferences<T extends PagePreferences>(
  pageKey: string,
  defaults: T
) {
  const { user } = useAuth();
  const authUserId = Number(user?.id_user || 0);
  const [storageUserId, setStorageUserId] = useState(() => getUserId());
  const idUser = authUserId || storageUserId;
  const defaultsRef = useRef(defaults);
  const defaultsPageKeyRef = useRef(pageKey);
  if (defaultsPageKeyRef.current !== pageKey) {
    defaultsRef.current = defaults;
    defaultsPageKeyRef.current = pageKey;
  }
  const stableDefaults = defaultsRef.current;
  const [preferences, setPreferencesState] = useState<T>(() =>
    readLocalPreferences(idUser, pageKey, stableDefaults)
  );
  const [loaded, setLoaded] = useState(false);
  const dirtyKeysRef = useRef<Set<keyof T>>(new Set());
  const debounceRef = useRef<number | null>(null);
  const latestPreferencesRef = useRef<T>(preferences);

  useEffect(() => {
    const refreshUserId = () => {
      const nextIdUser = getUserId();
      setStorageUserId((current) => (current === nextIdUser ? current : nextIdUser));
    };

    refreshUserId();
    const interval = window.setInterval(refreshUserId, 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    setLoaded(false);
    dirtyKeysRef.current.clear();

    if (!idUser) {
      setPreferencesState(stableDefaults);
      setLoaded(true);
      return;
    }

    let cancelled = false;
    const storageKey = getStorageKey(idUser, pageKey);
    setPreferencesState(readLocalPreferences(idUser, pageKey, stableDefaults));

    async function loadPreferences() {
      try {
        const remotePreferences = await getPagePreferences<T>(idUser, pageKey);
        if (cancelled) return;

        setPreferencesState((current) => {
          const next = { ...stableDefaults, ...current, ...remotePreferences };

          dirtyKeysRef.current.forEach((key) => {
            next[key] = current[key];
          });

          localStorage.setItem(storageKey, JSON.stringify(next));
          return next as T;
        });
      } catch (error) {
        console.warn(`Unable to load page preferences for ${pageKey}:`, error);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    }

    loadPreferences();

    return () => {
      cancelled = true;
    };
  }, [idUser, pageKey, stableDefaults]);

  const flushPendingPreferences = useCallback(async () => {
    if (!idUser || dirtyKeysRef.current.size === 0) return;

    const keysToSave = Array.from(dirtyKeysRef.current);
    dirtyKeysRef.current.clear();
    const latestPreferences = latestPreferencesRef.current;

    const preferencesToSave = keysToSave.reduce((acc, key) => {
      acc[key] = latestPreferences[key];
      return acc;
    }, {} as Partial<T>);

    try {
      await savePagePreferences<T>({
        id_user: idUser,
        page_key: pageKey,
        preferences: preferencesToSave,
      });
    } catch (error) {
      console.warn(`Unable to save page preferences for ${pageKey}:`, error);
    }
  }, [idUser, pageKey]);

  const persist = useCallback(
    (next: T, changedKeys: Set<keyof T>) => {
      if (!idUser) return;

      const storageKey = getStorageKey(idUser, pageKey);
      localStorage.setItem(storageKey, JSON.stringify(next));
      latestPreferencesRef.current = next;

      changedKeys.forEach((key) => dirtyKeysRef.current.add(key));

      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => {
        debounceRef.current = null;
        void flushPendingPreferences();
      }, 350);
    },
    [flushPendingPreferences, idUser, pageKey]
  );

  const setPreference = useCallback(
    <K extends keyof T>(key: K, value: T[K]) => {
      setPreferencesState((current) => {
        const next = { ...current, [key]: value };
        persist(next, new Set([key]));
        return next;
      });
    },
    [persist]
  );

  const setPreferences = useCallback(
    (patch: Partial<T>) => {
      setPreferencesState((current) => {
        const next = { ...current, ...patch } as T;
        persist(next, new Set(Object.keys(patch) as Array<keyof T>));
        return next;
      });
    },
    [persist]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      void flushPendingPreferences();
    };
  }, [flushPendingPreferences]);

  return {
    preferences,
    setPreference,
    setPreferences,
    loaded,
  };
}
